import { useEffect, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { useAppConfig } from '../../hooks/useAppConfig';
import { useAuth } from '../../context/AuthContext';
import { fetchCurriculumData } from '../../services/dataApi';

function normalizeLevel(value) {
  return String(value || '').trim().toLowerCase();
}

function useCurriculum(homeLang, level, enabled) {
  const [curriculum, setCurriculum] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    setCurriculum(null);
    setError(null);
    fetchCurriculumData(homeLang, `${level}/curriculum.json`)
      .then((data) => {
        if (active) setCurriculum(data);
      })
      .catch(() => {
        if (active) setError('Unable to load this curriculum right now.');
      });
    return () => { active = false; };
  }, [homeLang, level, enabled]);

  return { curriculum, error };
}

function TopicList({ title, topics }) {
  if (!Array.isArray(topics) || topics.length === 0) return null;
  return (
    <div>
      <h4 className="text-[10px] font-black uppercase tracking-widest text-slate-400">{title}</h4>
      <p className="mt-1 text-xs leading-relaxed text-slate-600">{topics.join(' · ')}</p>
    </div>
  );
}

export default function CurriculumPage() {
  const { level: rawLevel } = useParams();
  const { languagePrefs } = useAuth();
  const config = useAppConfig(languagePrefs?.nativeLanguage);
  const level = normalizeLevel(rawLevel);
  const levels = Array.isArray(config.level)
    ? config.level.map(normalizeLevel).filter(Boolean)
    : [];
  const isAllowed = levels.includes(level);
  const { curriculum, error } = useCurriculum(languagePrefs?.targetLanguage, level, isAllowed);

  if (levels.length === 0) {
    return <div className="page-shell"><div className="h-64 animate-pulse rounded-3xl bg-slate-100" /></div>;
  }
  if (!isAllowed) return <Navigate to="/" replace />;

  const days = Array.isArray(curriculum?.days) ? curriculum.days : [];
  const goal = curriculum?.dailyGoal;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10 sm:px-6 lg:px-8">
      <main className="mx-auto max-w-5xl">
        <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-indigo-700 transition hover:text-indigo-900">
          <span aria-hidden="true">←</span> Back to learning path
        </Link>

        {error && <p className="mt-6 rounded-2xl border border-rose-200 bg-rose-50 p-5 text-sm font-semibold text-rose-700">{error}</p>}

        {!curriculum && !error && (
          <div className="mt-6 space-y-4" aria-label="Loading curriculum">
            <div className="h-48 animate-pulse rounded-3xl bg-slate-200" />
            <div className="h-32 animate-pulse rounded-2xl bg-white" />
          </div>
        )}

        {curriculum && (
          <>
            <section className="mt-6 overflow-hidden rounded-3xl bg-gradient-to-br from-indigo-700 via-violet-700 to-fuchsia-700 p-7 text-white shadow-xl sm:p-10">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-white/70">{curriculum.framework || 'CEFR-aligned'} curriculum</p>
              <h1 className="mt-3 text-4xl font-black sm:text-5xl">{curriculum.title || level.toUpperCase()}</h1>
              {curriculum.description && <p className="mt-4 max-w-2xl text-base leading-relaxed text-white/85">{curriculum.description}</p>}
              <div className="mt-7 flex flex-wrap gap-3 text-sm font-bold">
                {curriculum.durationDays && <span className="rounded-full bg-white/15 px-4 py-2">{curriculum.durationDays} days</span>}
                {goal?.minMinutes && <span className="rounded-full bg-white/15 px-4 py-2">{goal.minMinutes}–{goal.maxMinutes || goal.minMinutes} min/day</span>}
                {curriculum.skills?.length > 0 && <span className="rounded-full bg-white/15 px-4 py-2">{curriculum.skills.length} skills</span>}
              </div>
            </section>

            <section className="mt-8">
              <h2 className="text-2xl font-black text-slate-900">Daily learning plan</h2>
              <p className="mt-1 text-sm text-slate-600">Follow each day in order, or use it as a guide alongside the learning path.</p>
              <div className="mt-5 grid gap-4 md:grid-cols-2">
                {days.map((day) => (
                  <article key={day.day} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex items-start gap-3">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-indigo-100 text-sm font-black text-indigo-700">{day.day}</span>
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-indigo-500">{day.theme || `Day ${day.day}`}</p>
                        <h3 className="mt-1 text-base font-black text-slate-900">{day.title}</h3>
                      </div>
                    </div>
                    {day.objective && <p className="mt-4 text-sm leading-relaxed text-slate-600">{day.objective}</p>}
                    <div className="mt-4 space-y-3 border-t border-slate-100 pt-4">
                      <TopicList title="Vocabulary" topics={day.vocabularyTopics} />
                      <TopicList title="Grammar" topics={day.grammarTopics} />
                      <TopicList title="Skills" topics={day.skills} />
                    </div>
                  </article>
                ))}
              </div>
            </section>
          </>
        )}
      </main>
    </div>
  );
}
